
/**
 * Event-driven permission cache invalidation system
 * Handles permission change events and cache invalidation
 */

type PermissionEventType = 
  | 'admin_granted'
  | 'admin_revoked'
  | 'subscription_updated'
  | 'subscription_expired'
  | 'manual_refresh';

interface PermissionEventMetadata {
  isGranted?: boolean;
  expired?: boolean;
  [key: string]: unknown;
}

interface PermissionEvent {
  type: PermissionEventType;
  userId: string;
  timestamp: number;
  source: 'admin_action' | 'stripe_webhook' | 'expiry_check' | 'manual';
  metadata?: PermissionEventMetadata;
}

type EventListener = (event: PermissionEvent) => void;

class PermissionEventBus {
  private listeners: Map<PermissionEventType, Set<EventListener>> = new Map();
  private globalListeners: Set<EventListener> = new Set();

  /**
   * Subscribe to specific permission events
   */
  on(eventType: PermissionEventType, listener: EventListener): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    
    this.listeners.get(eventType)!.add(listener);
    
    // Return unsubscribe function
    return () => {
      this.listeners.get(eventType)?.delete(listener);
    };
  }

  /**
   * Subscribe to all permission events
   */
  onAny(listener: EventListener): () => void {
    this.globalListeners.add(listener);
    
    return () => {
      this.globalListeners.delete(listener);
    };
  }

  /**
   * Emit a permission event
   */
  emit(event: PermissionEvent): void {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔄 Permission event emitted:', event);
    }
    
    // Notify specific event listeners
    const specificListeners = this.listeners.get(event.type);
    if (specificListeners) {
      specificListeners.forEach(listener => {
        try {
          listener(event);
        } catch (error) {
          console.error('Error in permission event listener:', error);
        }
      });
    }
    
    // Notify global listeners
    this.globalListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Error in global permission event listener:', error);
      }
    });
  }

  /**
   * Emit admin status change event
   */
  emitAdminChange(userId: string, isGranted: boolean, source: 'admin_action' = 'admin_action'): void {
    this.emit({
      type: isGranted ? 'admin_granted' : 'admin_revoked',
      userId,
      timestamp: Date.now(),
      source,
      metadata: { isGranted }
    });
  }

  /**
   * Emit subscription update event
   */
  emitSubscriptionUpdate(
    userId: string,
    subscriptionData: PermissionEventMetadata,
    source: 'stripe_webhook' | 'manual' = 'stripe_webhook'
  ): void {
    this.emit({
      type: 'subscription_updated',
      userId,
      timestamp: Date.now(),
      source,
      metadata: subscriptionData
    });
  }

  /**
   * Emit subscription expiry event
   */
  emitSubscriptionExpiry(userId: string, source: 'expiry_check' = 'expiry_check'): void {
    this.emit({
      type: 'subscription_expired',
      userId,
      timestamp: Date.now(),
      source,
      metadata: { expired: true }
    });
  }

  /**
   * Clear all listeners (for cleanup)
   */
  clear(): void {
    this.listeners.clear();
    this.globalListeners.clear();
  }
}

// Export singleton instance
export const permissionEventBus = new PermissionEventBus();
export type { PermissionEvent, PermissionEventType };
